<template>
  <div class="provider-role-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">PREIKA PROVIDER SECURITY</p>
        <h1>運営権限管理</h1>
        <p>
          PREIKA運営管理者、サポート閲覧者、緊急最高管理者を画面から設定します。
        </p>
      </div>
      <CButton color="secondary" variant="outline" :disabled="loading || saving" @click="loadAll">
        再読込
      </CButton>
    </header>

    <CAlert color="warning" class="security-alert">
      この画面は<strong>PREIKA緊急最高管理者</strong>だけが利用できます。
      権限変更のたびに現在のパスワードと変更理由を確認し、操作履歴へ記録します。
      緊急最高管理者は日常業務用ではありません。
    </CAlert>
    <CAlert v-if="superAdminCount <= 1 && !loading" color="danger" class="security-alert">
      有効なPREIKA緊急最高管理者が1名だけです。復旧不能を防ぐため、信頼できる別担当者を追加してください。
      最後の1名と自分自身の緊急最高権限は解除できません。
    </CAlert>
    <!--
      画面上端に出すのは、読み込みの失敗だけ（2026-09-26）。
      付与・解除の結果は、押した人の行（断られたときは開いている小窓の中）に出す。
      上端だと、小窓の暗幕の下や、一覧の下のほうから見えなかった。
    -->
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <section class="summary-grid" aria-label="PREIKA運営権限の概要">
      <article>
        <span>緊急最高管理者</span>
        <strong>{{ roleCount('PLATFORM_SUPER_ADMIN') }}</strong>
        <small>BREAK-GLASS</small>
      </article>
      <article>
        <span>運営管理者</span>
        <strong>{{ roleCount('PLATFORM_OPERATIONS_ADMIN') }}</strong>
        <small>DAILY OPERATIONS</small>
      </article>
      <article>
        <span>サポート閲覧者</span>
        <strong>{{ roleCount('PLATFORM_SUPPORT_VIEWER') }}</strong>
        <small>READ ONLY</small>
      </article>
    </section>

    <section class="role-guide-grid" aria-label="PREIKA運営権限の説明">
      <article v-for="role in normalizedRoles" :key="role.roleCode" class="role-guide-card">
        <div class="role-guide-card__heading">
          <CBadge :color="roleColor(role.roleCode)">{{ roleShortLabel(role.roleCode) }}</CBadge>
          <strong>{{ role.roleName }}</strong>
        </div>
        <!-- 画面の説明を先に出す。DB の説明には「Feature」「Provider Role」など開発側の言葉が残っている（2026-09-26） -->
        <p>{{ roleDescription(role.roleCode) || role.description }}</p>
        <small>{{ roleUsage(role.roleCode) }}</small>
      </article>
    </section>

    <CCard class="management-card">
      <CCardHeader class="section-header">
        <div>
          <strong>AKI運営担当者</strong>
          <small>利用中のAKI社員だけを表示します。</small>
        </div>
      </CCardHeader>
      <CCardBody>
        <div v-if="loading" class="loading-state"><CSpinner color="primary"/></div>
        <template v-else>
          <div class="desktop-only table-shell">
            <CDataTable
              :items="normalizedUsers"
              :fields="userFields"
              bordered
              striped
              hover
              pagination
              :items-per-page="10"
              no-items-view="対象利用者がいません。"
            >
              <template #user="{item}">
                <td>
                  <div class="user-cell">
                    <strong>{{ item.name }}</strong>
                    <span>{{ item.email }}</span>
                    <CBadge v-if="item.userId === accessContext.userId" color="info">現在ログイン中</CBadge>
                  </div>
                </td>
              </template>
              <template #providerRoles="{item}">
                <td>
                  <div v-if="item.roleCodes.length" class="role-chip-list">
                    <span v-for="roleCode in item.roleCodes" :key="roleCode" class="role-chip">
                      <CBadge :color="roleColor(roleCode)">{{ roleName(roleCode) }}</CBadge>
                      <button
                        type="button"
                        class="role-chip__remove"
                        :disabled="saving || !canRemoveRole(item, roleCode)"
                        :title="removeDisabledReason(item, roleCode)"
                        @click="openRemove(item, roleCode)"
                      >
                        解除
                      </button>
                    </span>
                  </div>
                  <span v-else class="no-role-label">運営権限なし</span>
                  <!-- 付与・解除の結果。付いた・外れた札と同じ欄に出す。 -->
                  <ActionResultMessage v-bind="rowMessageOf(item.userId)"/>
                </td>
              </template>
              <template #actions="{item}">
                <td class="action-cell">
                  <CButton
                    size="sm"
                    color="primary"
                    variant="outline"
                    :disabled="saving || !availableRoles(item).length"
                    @click="openAssign(item)"
                  >
                    運営権限を追加
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <div class="mobile-only mobile-list" aria-label="AKI運営担当者一覧">
            <article v-for="user in normalizedUsers" :key="user.userId" class="user-card">
              <div class="user-card__header">
                <div>
                  <strong>{{ user.name }}</strong>
                  <span>{{ user.email }}</span>
                </div>
                <CBadge v-if="user.userId === accessContext.userId" color="info">自分</CBadge>
              </div>

              <div v-if="user.roleCodes.length" class="mobile-role-list">
                <div v-for="roleCode in user.roleCodes" :key="roleCode" class="mobile-role-row">
                  <CBadge :color="roleColor(roleCode)">{{ roleName(roleCode) }}</CBadge>
                  <CButton
                    size="sm"
                    color="danger"
                    variant="outline"
                    :disabled="saving || !canRemoveRole(user, roleCode)"
                    :title="removeDisabledReason(user, roleCode)"
                    @click="openRemove(user, roleCode)"
                  >
                    解除
                  </CButton>
                </div>
              </div>
              <div v-else class="no-role-box">運営権限は設定されていません。</div>
              <ActionResultMessage v-bind="rowMessageOf(user.userId)"/>

              <CButton
                class="mobile-add-action"
                color="primary"
                variant="outline"
                :disabled="saving || !availableRoles(user).length"
                @click="openAssign(user)"
              >
                運営権限を追加
              </CButton>
            </article>
            <div v-if="!normalizedUsers.length" class="empty-state">対象利用者がいません。</div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CModal
      :title="modalMode === 'assign' ? '運営権限を追加' : '運営権限を解除'"
      :show="roleModal"
      @update:show="closeModal"
      :close-on-backdrop="false"
      class="provider-role-modal"
    >
      <template v-if="selectedUser">
        <div class="selected-user-box">
          <span>対象者</span>
          <strong>{{ selectedUser.name }}</strong>
          <small>{{ selectedUser.email }}</small>
        </div>

        <!--
          **CSelect を v-model で結ばない。**CoreUI が input と change を
          明示的に捨てているので、v-model は一度も呼ばれない。
          結んでいたころは、開いたときに入る「先頭の役割」から二度と
          動かず、**選び直しても最上位権限が付いた。**
        -->
        <CSelect
          v-if="modalMode === 'assign'"
          label="設定する権限グループ"
          :options="assignRoleOptions"
          :value="form.roleCode"
          @update:value="value => { form.roleCode = selectedValue(value) }"
        />
        <div v-else class="selected-role-box">
          <span>解除する権限グループ</span>
          <CBadge :color="roleColor(form.roleCode)">{{ roleName(form.roleCode) }}</CBadge>
        </div>

        <CAlert v-if="form.roleCode === 'PLATFORM_SUPER_ADMIN'" color="warning">
          PREIKA緊急最高管理者は復旧専用の最上位権限です。日常業務担当者へは付与しないでください。
        </CAlert>

        <CInput
          label="現在のパスワード"
          type="password"
          autocomplete="current-password"
          v-model="form.currentPassword"
          description="操作する緊急最高管理者本人を再認証します。"
        />
        <CTextarea
          label="変更理由"
          rows="4"
          maxlength="500"
          v-model.trim="form.reason"
          description="10文字以上500文字以内。監査ログへ記録します。"
        />
        <!--
          断られた理由は、小窓を開いたまま、押したボタンのすぐ上に出す（2026-09-26）。
          前は画面上端に出していて、暗幕の下で読めなかった。
        -->
        <ActionResultMessage :text="modalError" type="danger"/>
      </template>

      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" :disabled="saving" @click="closeModal">キャンセル</CButton>
          <CButton
            :color="modalMode === 'assign' ? 'primary' : 'danger'"
            :disabled="saving"
            @click="submitRoleChange"
          >
            {{ saving ? '処理しています' : submitLabel }}
          </CButton>
        </div>
      </template>
    </CModal>
  </div>
</template>

<script>
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import { clearAccessContext, getAccessContext } from '@/services/accessContext'
import providerRoleApi from '@/services/providerRoleManagement'
import { canManageProviderRoles } from '@/utils/accessPolicy'
import { selectedValue } from '@/utils/coreuiSelect'

const ROLE_LABELS = {
  PLATFORM_SUPER_ADMIN: 'PREIKA緊急最高管理者',
  PLATFORM_OPERATIONS_ADMIN: 'PREIKA運営管理者',
  PLATFORM_SUPPORT_VIEWER: 'PREIKAサポート閲覧者'
}

const ROLE_SHORT_LABELS = {
  PLATFORM_SUPER_ADMIN: '緊急復旧',
  PLATFORM_OPERATIONS_ADMIN: '日常運用',
  PLATFORM_SUPPORT_VIEWER: '読み取り専用'
}

const emptyForm = () => ({
  roleCode: '',
  currentPassword: '',
  reason: ''
})

export default {
  name: 'ProviderRoleManagement',
  components: { ActionResultMessage },
  data () {
    return {
      loading: false,
      saving: false,
      users: [],
      roles: [],
      accessContext: { userId: null, roles: [] },
      /* 画面の読み込みの失敗だけ（上端に出す）。 */
      errorMessage: '',
      /* 付与・解除の結果。キーは利用者。その人の行に出す。 */
      rowMessages: {},
      /* 小窓の中で断られた理由。小窓を閉じずに中へ出す。 */
      modalError: '',
      roleModal: false,
      modalMode: 'assign',
      selectedUser: null,
      form: emptyForm(),
      userFields: [
        { key: 'user', label: '利用者' },
        { key: 'providerRoles', label: '現在の運営権限' },
        { key: 'actions', label: '' }
      ]
    }
  },
  computed: {
    normalizedRoles () {
      return this.roles.map(item => ({
        id: Number(this.value(item, 'id')),
        roleCode: String(this.value(item, 'roleCode', 'role_code') || ''),
        roleName: this.value(item, 'roleName', 'role_name') || '名称未設定',
        description: this.value(item, 'description') || '',
        displayOrder: Number(this.value(item, 'displayOrder', 'display_order') || 999)
      })).filter(role => role.roleCode)
        .sort((left, right) => left.displayOrder - right.displayOrder)
    },
    normalizedUsers () {
      return this.users.map(item => ({
        userId: Number(this.value(item, 'userId', 'user_id')),
        name: this.value(item, 'name') || '氏名未設定',
        email: this.value(item, 'email') || 'メールアドレス未設定',
        activationStatus: String(this.value(item, 'activationStatus', 'activation_status') || ''),
        roleCodes: this.arrayValue(item, 'roleCodes', 'role_codes')
          .filter(roleCode => Object.prototype.hasOwnProperty.call(ROLE_LABELS, roleCode))
      })).filter(user => Number.isFinite(user.userId) && user.userId > 0 && user.activationStatus === 'ACTIVE')
    },
    superAdminCount () {
      return this.roleCount('PLATFORM_SUPER_ADMIN')
    },
    assignRoleOptions () {
      if (!this.selectedUser) return [{ value: '', label: '権限グループを選択' }]
      return [{ value: '', label: '権限グループを選択' }].concat(
        this.availableRoles(this.selectedUser).map(role => ({
          value: role.roleCode,
          label: role.roleName
        }))
      )
    },
    submitLabel () {
      return this.modalMode === 'assign'
        ? '再認証して運営権限を追加'
        : '再認証して運営権限を解除'
    }
  },
  created () {
    this.loadAll()
  },
  methods: {
    /**
     * **テンプレートから使うので methods に置く。**
     * import しただけの関数は、テンプレートからは見えない。
     */
    selectedValue,

    value (item, ...keys) {
      for (const key of keys) {
        if (item && Object.prototype.hasOwnProperty.call(item, key)) return item[key]
        const upper = key.toUpperCase()
        if (item && Object.prototype.hasOwnProperty.call(item, upper)) return item[upper]
      }
      return null
    },
    arrayValue (item, ...keys) {
      const value = this.value(item, ...keys)
      if (Array.isArray(value)) return value.map(item => String(item))
      if (typeof value === 'string') {
        return value.replace(/[{}]/g, '').split(',').map(item => item.trim()).filter(Boolean)
      }
      return []
    },
    /**
     * 一覧を読み直す。
     *
     * 付与・解除のあとは quiet で呼ぶ。回転に差し替えると表が作り直されて
     * 1ページ目へ戻り、押した人の行とその結果が見えなくなる。
     * （［再読込］からは押したときの出来事が渡るので、quiet は立たない。）
     */
    async loadAll (options) {
      const quiet = !!(options && options.quiet === true)
      if (!quiet) this.loading = true
      this.errorMessage = ''
      try {
        const context = await getAccessContext({ force: true })
        if (!canManageProviderRoles(context)) {
          await this.$router.replace({ name: 'AccessDenied' }).catch(() => {})
          return
        }
        this.accessContext = context
        const [users, roles] = await Promise.all([
          providerRoleApi.getUsers(),
          providerRoleApi.getRoles()
        ])
        if (!Array.isArray(users) || !Array.isArray(roles)) {
          throw new Error('PREIKA運営権限の情報を読み込めませんでした。時間をおいて再度お試しください。')
        }
        this.users = users
        this.roles = roles
      } catch (error) {
        this.errorMessage = this.errorFrom(error, 'PREIKA運営権限を取得できませんでした。画面を再読み込みしてください。')
      } finally {
        if (!quiet) this.loading = false
      }
    },
    /** その人の行に出す結果。無ければ空（部品は何も出さない）。 */
    rowMessageOf (userId) {
      return this.rowMessages[userId] || { type: 'success', text: '' }
    },
    availableRoles (user) {
      const assigned = new Set(user.roleCodes || [])
      return this.normalizedRoles.filter(role => !assigned.has(role.roleCode))
    },
    roleCount (roleCode) {
      return this.normalizedUsers.filter(user => user.roleCodes.includes(roleCode)).length
    },
    roleName (roleCode) {
      const role = this.normalizedRoles.find(item => item.roleCode === roleCode)
      return role ? role.roleName : (ROLE_LABELS[roleCode] || roleCode)
    },
    roleShortLabel (roleCode) {
      return ROLE_SHORT_LABELS[roleCode] || 'Provider'
    },
    roleColor (roleCode) {
      return {
        PLATFORM_SUPER_ADMIN: 'danger',
        PLATFORM_OPERATIONS_ADMIN: 'primary',
        PLATFORM_SUPPORT_VIEWER: 'secondary'
      }[roleCode] || 'secondary'
    },
    roleDescription (roleCode) {
      return {
        PLATFORM_SUPER_ADMIN: '重大障害や運営管理者の喪失時だけに使用する、復旧用の権限グループです。',
        PLATFORM_OPERATIONS_ADMIN: '契約企業の開通、契約、プラン、利用可能機能、最初の管理者招待を担当します。',
        PLATFORM_SUPPORT_VIEWER: '問い合わせ調査に必要な契約内容・利用可能機能・監査情報を、読み取り専用で確認します。'
      }[roleCode] || ''
    },
    roleUsage (roleCode) {
      return {
        PLATFORM_SUPER_ADMIN: '通常運用では使用しません。',
        PLATFORM_OPERATIONS_ADMIN: '運営側の権限グループそのものの付与・解除はできません。',
        PLATFORM_SUPPORT_VIEWER: '作成・更新・招待送信はできません。'
      }[roleCode] || ''
    },
    canRemoveRole (user, roleCode) {
      if (roleCode !== 'PLATFORM_SUPER_ADMIN') return true
      if (user.userId === Number(this.accessContext.userId)) return false
      return this.superAdminCount > 1
    },
    removeDisabledReason (user, roleCode) {
      if (roleCode !== 'PLATFORM_SUPER_ADMIN') return ''
      if (user.userId === Number(this.accessContext.userId)) return '自分自身の緊急最高権限は解除できません。'
      if (this.superAdminCount <= 1) return '最後の緊急最高管理者は解除できません。先に別の担当者を緊急最高管理者として追加してください。'
      return ''
    },
    openAssign (user) {
      const available = this.availableRoles(user)
      if (!available.length) return
      this.selectedUser = user
      this.modalMode = 'assign'
      this.form = emptyForm()
      this.form.roleCode = available[0].roleCode
      /* 前回断られた理由を残さない。開いた瞬間に赤い字があると、いま断られたと読める。 */
      this.modalError = ''
      this.roleModal = true
    },
    openRemove (user, roleCode) {
      if (!this.canRemoveRole(user, roleCode)) return
      this.selectedUser = user
      this.modalMode = 'remove'
      this.form = emptyForm()
      this.form.roleCode = roleCode
      this.modalError = ''
      this.roleModal = true
    },
    closeModal () {
      /*
       * 送っているあいだは閉じない（2026-09-26 監査）。見出しの×もここへ来る。
       * 閉じると、あとから届いた理由を出す場所がなくなる。
       */
      if (this.saving) return
      this.form.currentPassword = ''
      this.modalError = ''
      this.roleModal = false
      this.selectedUser = null
    },
    validateForm () {
      if (!this.selectedUser) throw new Error('対象利用者を選択してください。')
      if (!this.form.roleCode) throw new Error('設定する権限グループを選択してください。')
      if (!this.form.currentPassword) throw new Error('現在のパスワードを入力してください。')
      if (!this.form.reason || this.form.reason.length < 10 || this.form.reason.length > 500) {
        throw new Error('変更理由は10文字以上500文字以内で入力してください。')
      }
    },
    async submitRoleChange () {
      this.saving = true
      this.modalError = ''
      /*
       * 変えたあとの読み直しで落ちたときは、小窓はもう閉じている。
       * その理由は loadAll が画面上端（読み込みの失敗）へ書く。
       * 小窓の中へ書くのは、変える前に断られたときだけ。
       */
      let changed = false
      try {
        this.validateForm()
        const target = this.selectedUser
        const payload = {
          targetUserId: target.userId,
          roleCode: this.form.roleCode,
          currentPassword: this.form.currentPassword,
          reason: this.form.reason
        }
        /* その人の行の前の結果は消す。残ると、いま押した結果に見える。 */
        this.$delete(this.rowMessages, target.userId)
        let text
        if (this.modalMode === 'assign') {
          await providerRoleApi.assignRole(payload)
          text = `${target.name}さんへ${this.roleName(this.form.roleCode)}を設定しました。`
        } else {
          await providerRoleApi.removeRole(payload)
          text = `${target.name}さんの${this.roleName(this.form.roleCode)}を解除しました。`
        }
        changed = true
        this.$set(this.rowMessages, target.userId, { type: 'success', text })
        this.form.currentPassword = ''
        this.roleModal = false
        this.selectedUser = null
        clearAccessContext()
        await this.loadAll({ quiet: true })
      } catch (error) {
        this.form.currentPassword = ''
        if (!changed) {
          this.modalError = this.errorFrom(error, 'PREIKA運営権限を変更できませんでした。時間をおいて再度お試しください。')
        }
      } finally {
        this.saving = false
      }
    },
    errorFrom (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object') return data.message || fallback
      if (typeof data === 'string' && data) return data
      // axiosが付ける英語のmessage（Network Error など）は画面へ出さない。
      // 画面側が自分で投げたErrorの日本語だけを通す。
      if (error && !error.isAxiosError && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style scoped>
.provider-role-page { min-width: 0; padding: 26px; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; gap: 20px; }
.page-header > div { min-width: 0; }
.page-header h1 { margin: 0 0 8px; color: #17384d; }
.page-header p { max-width: 900px; margin: 0; color: #607681; line-height: 1.75; overflow-wrap: anywhere; }
.page-header .btn { min-height: 44px; flex: 0 0 auto; }
.eyebrow { margin: 0 0 4px; color: #0a78b9; font-size: 12px; font-weight: 900; letter-spacing: .14em; }
.security-alert { line-height: 1.75; }
.summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); margin: 18px 0; gap: 14px; }
.summary-grid article { display: grid; min-width: 0; padding: 18px; border: 1px solid #d9e5ec; border-radius: 14px; background: #fff; box-shadow: 0 8px 24px rgba(25, 65, 86, .06); gap: 2px; }
.summary-grid span { color: #657b88; font-size: 12px; font-weight: 800; }
.summary-grid strong { color: #17384d; font-size: 32px; line-height: 1.2; }
.summary-grid small { color: #8b9ca5; font-size: 12px; font-weight: 800; letter-spacing: .09em; }
.role-guide-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); margin-bottom: 18px; gap: 12px; }
.role-guide-card { min-width: 0; padding: 16px; border: 1px solid #dbe6ec; border-radius: 13px; background: #fff; }
.role-guide-card__heading { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.role-guide-card__heading strong { color: #17384d; }
.role-guide-card p { margin: 11px 0 6px; color: #526b79; line-height: 1.65; overflow-wrap: anywhere; }
.role-guide-card small { color: #7b8d97; }
.management-card { min-width: 0; }
.section-header { display: flex; justify-content: space-between; gap: 12px; }
.section-header > div { display: grid; flex: 1 1 auto; min-width: 0; gap: 3px; }
.section-header small { color: #687d89; overflow-wrap: anywhere; }
.loading-state { padding: 48px 0; text-align: center; }
.table-shell { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
/*
  表の最小の幅。画面の幅 1280px のとき表の入る幅は約 845px なので、850px だと右端のボタンの欄が
  枠の外へ出ていた。列は3つ（利用者 190px 以上・運営権限の札・ボタン）で、760px あれば札もボタンも重ならない。
  会社側の権限管理の表と同じ値。
*/
.table-shell::v-deep table { min-width: 760px; }
.user-cell { display: grid; min-width: 190px; gap: 3px; }
.user-cell strong { color: #17384d; }
.user-cell span { color: #657987; font-size: 12px; overflow-wrap: anywhere; }
.user-cell .badge { width: fit-content; margin-top: 4px; }
.role-chip-list { display: flex; flex-wrap: wrap; gap: 8px; }
.role-chip { display: inline-flex; align-items: center; padding: 3px 4px 3px 7px; border: 1px solid #d6e2e9; border-radius: 999px; background: #f8fafb; gap: 6px; }
.role-chip__remove { min-height: 30px; padding: 3px 8px; border: 0; border-radius: 999px; background: transparent; color: #b02a37; font-size: 12px; font-weight: 800; }
.role-chip__remove:disabled { color: #98a5ac; cursor: not-allowed; }
.no-role-label { color: #7d8f98; font-size: 12px; }
.action-cell { white-space: nowrap; }
/*
  表とカードは同じ中身で、画面幅に応じて片方だけを出す。

  切り替えは .mobile-only / .desktop-only が持っている。ところが同じ div に
  付けた一覧クラスにも display があり、後ろに書いた方が勝つため、広い画面
  でも表の下にカードが並んでいた。組み合わせで明示して、書き順に左右され
  ないようにする。
*/
.mobile-only { display: none; }
.mobile-only.mobile-list { display: none; }
.mobile-list { display: grid; gap: 12px; }
.user-card { min-width: 0; padding: 15px; border: 1px solid #d8e4ec; border-radius: 13px; background: #fff; box-shadow: 0 6px 18px rgba(28, 68, 91, .06); }
.user-card__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.user-card__header > div { display: grid; min-width: 0; gap: 3px; }
.user-card__header strong { color: #17384d; overflow-wrap: anywhere; }
.user-card__header span { color: #657987; font-size: 12px; overflow-wrap: anywhere; }
.mobile-role-list { display: grid; margin-top: 14px; gap: 8px; }
.mobile-role-row { display: flex; align-items: center; justify-content: space-between; padding: 10px; border: 1px solid #e0e8ed; border-radius: 10px; gap: 10px; }
.mobile-role-row .btn { min-height: 42px; margin: 0; }
.no-role-box { margin-top: 14px; padding: 12px; border-radius: 9px; background: #f4f7f9; color: #6e818d; font-size: 12px; }
.mobile-add-action { width: 100%; min-height: 44px; margin-top: 14px; white-space: normal; }
.empty-state { padding: 28px 14px; border: 1px dashed #cad8e1; border-radius: 12px; color: #687d89; text-align: center; }
.selected-user-box { display: grid; margin-bottom: 16px; padding: 14px; border-radius: 11px; background: #f3f8fb; gap: 2px; }
.selected-user-box span, .selected-role-box span { color: #6c7f8a; font-size: 12px; font-weight: 800; }
.selected-user-box strong { color: #17384d; }
.selected-user-box small { color: #657987; overflow-wrap: anywhere; }
.selected-role-box { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; padding: 14px; border: 1px solid #dce7ed; border-radius: 11px; gap: 12px; }
.modal-actions { display: flex; justify-content: flex-end; width: 100%; gap: 9px; }
.modal-actions .btn { min-height: 44px; }

@media (max-width: 767.98px) {
  .provider-role-page { padding: 12px 0 92px; }
  .page-header { flex-direction: column; gap: 14px; }
  .page-header h1 { font-size: 28px; line-height: 1.25; }
  .page-header .btn { width: 100%; }
  .summary-grid, .role-guide-grid { grid-template-columns: minmax(0, 1fr); gap: 10px; }
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .mobile-only.mobile-list { display: grid; }
  .modal-actions { flex-direction: column-reverse; }
  .modal-actions .btn { width: 100%; margin: 0; white-space: normal; }
  .provider-role-page::v-deep .provider-role-modal .modal-dialog { width: auto; max-width: calc(100vw - 16px); margin: 8px; }
  .provider-role-page::v-deep .provider-role-modal .modal-body { max-height: calc(100vh - 190px); overflow-y: auto; }
}

@media (min-width: 768px) {
  .desktop-only { display: block; }
}
</style>
